import React, { useState, useEffect, useMemo } from "react";
import useNav from "./hooks/useNav";
import PrimaryButton from "./components/PrimaryButton";
import Header from "./components/Header";
import { Loader, Minus, Plus, UtensilsCrossed, Users, Clock, MessageSquare, MapPin, AlertCircle, Bike, QrCode, CreditCard, Banknote } from "lucide-react";
import BackButton from "./components/BackButton";
import { useCart } from "./context/CartContext.jsx";
import { useRestaurant } from "./context/RestaurantContext.jsx";
import { useUser } from "./context/UserContext.jsx";
import appConfig from "./config";
import { toast } from "react-toastify";
import { useAuthFetch } from "./hooks/useAuthFetch";

const GUEST_NAME_KEY = "guest_name";
const GUEST_PHONE_KEY = "guest_phone";

const SectionCard = ({ icon: Icon, title, children }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
        <div className="flex items-center gap-2.5 mb-4">
            <div className="w-7 h-7 rounded-lg bg-brandBlue/10 flex items-center justify-center shrink-0">
                <Icon size={14} className="text-brandBlue" />
            </div>
            <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        </div>
        {children}
    </div>
);

const SegmentedControl = ({ options, value, onChange, disabled }) => (
    <div className="flex bg-gray-100 rounded-2xl p-1 gap-1">
        {options.map(opt => (
            <button
                key={opt.value}
                onClick={() => onChange(opt.value)}
                disabled={disabled}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                    value === opt.value
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-400 hover:text-gray-600"
                } disabled:opacity-50`}
            >
                {opt.label}
            </button>
        ))}
    </div>
);

const fieldClass = "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:border-brandBlue focus:bg-white transition-colors disabled:opacity-50";

function generateDeliverySlots(hoursStart, hoursEnd, bufferMinutes) {
    const now = new Date();
    const [startH, startM] = hoursStart.split(':').map(Number);
    const [endH, endM] = hoursEnd.split(':').map(Number);
    const deliveryStart = startH * 60 + startM;
    const deliveryEnd = endH * 60 + endM;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const roundedEarliest = Math.ceil((nowMinutes + bufferMinutes) / 30) * 30;
    let slotMinutes = Math.max(deliveryStart, roundedEarliest);
    const slots = [];
    while (slotMinutes < deliveryEnd) {
        const h = Math.floor(slotMinutes / 60);
        const m = slotMinutes % 60;
        slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        slotMinutes += 30;
    }
    return slots;
}

const OrderPage = () => {
    const { restaurant } = useRestaurant();

    const cashPaymentEnabled = restaurant?.settings?.cash_payment_enabled ?? true;

    const [orderType, setOrderType] = useState("restaurant");
    const [paymentMethod, setPaymentMethod] = useState(() =>
        (restaurant?.settings?.cash_payment_enabled ?? true) ? "cash" : "online"
    );
    const [time, setTime] = useState("asap");
    const [customTime, setCustomTime] = useState("");
    const [selectedSlot, setSelectedSlot] = useState(null); // null = asap, "HH:MM" = конкретный слот
    const [peopleCount, setPeopleCount] = useState(1);
    const [comment, setComment] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Поля адреса доставки
    const [street, setStreet] = useState("");
    const [apartment, setApartment] = useState("");
    const [entrance, setEntrance] = useState("");
    const [floor, setFloor] = useState("");

    const [guestName, setGuestName] = useState(() => localStorage.getItem(GUEST_NAME_KEY) || "");
    const [guestPhone, setGuestPhone] = useState(() => localStorage.getItem(GUEST_PHONE_KEY) || "");

    const navigate = useNav();
    const { totalPrice, cartItems, clearCart, tableId, tableName } = useCart();
    const { user, isAuthenticated, login } = useUser();
    const { authFetch, loading: authLoading } = useAuthFetch();

    // Настройки доставки из settings ресторана
    const deliveryCost       = restaurant?.settings?.delivery_cost         ?? 0;
    const minOrderAmount     = restaurant?.settings?.min_order_amount      ?? 0;
    const deliveryEnabled    = restaurant?.settings?.delivery_enabled      ?? true;
    const isDelivery     = orderType === "delivery";
    const belowMinOrder  = isDelivery && minOrderAmount > 0 && totalPrice < minOrderAmount;
    const totalWithDelivery = isDelivery && deliveryCost > 0 ? totalPrice + deliveryCost : totalPrice;

    const deliveryHoursStart  = restaurant?.settings?.delivery_hours_start ?? '10:00';
    const deliveryHoursEnd    = restaurant?.settings?.delivery_hours_end   ?? '22:00';
    const deliverySlotBuffer  = restaurant?.settings?.delivery_slot_buffer ?? 30;
    const workingHoursStart   = restaurant?.settings?.working_hours_start  ?? '10:00';
    const workingHoursEnd     = restaurant?.settings?.working_hours_end    ?? '23:00';

    // Минимальное время для ввода — позже текущего момента в рамках рабочих часов
    const minRestaurantTime = (() => {
        const now = new Date();
        const h = String(now.getHours()).padStart(2, '0');
        const m = String(now.getMinutes()).padStart(2, '0');
        const nowStr = `${h}:${m}`;
        return nowStr > workingHoursStart ? nowStr : workingHoursStart;
    })();

    const maxCookingTime = useMemo(
        () => cartItems.reduce((max, item) => Math.max(max, item.cooking_time ?? 0), 0),
        [cartItems]
    );

    const deliverySlots = useMemo(() => {
        if (!isDelivery) return [];
        return generateDeliverySlots(deliveryHoursStart, deliveryHoursEnd, deliverySlotBuffer + maxCookingTime);
    }, [isDelivery, deliveryHoursStart, deliveryHoursEnd, deliverySlotBuffer, maxCookingTime]);

    useEffect(() => {
        if (isSubmitting) return;
        if (cartItems.length === 0) {
            toast.error("Корзина пуста");
            navigate("/cart");
        }
    }, [cartItems.length, isSubmitting, navigate]);

    // Если доставка отключена в ресторане или открыт по QR столика — сбрасываем на «в ресторане»
    useEffect(() => {
        if ((!deliveryEnabled || tableId) && orderType === "delivery") setOrderType("restaurant");
    }, [deliveryEnabled, tableId]);


    // Сбрасываем слот при переключении типа заказа
    useEffect(() => {
        if (!isDelivery) setSelectedSlot(null);
    }, [isDelivery]);

    const buildDeliveryPayload = () => {
        if (!isDelivery) return false;
        return {
            enabled: true,
            address: street.trim(),
            apartment: apartment.trim(),
            entrance: entrance.trim(),
            floor: floor.trim(),
            cost: deliveryCost,
        };
    };

    const validateForm = () => {
        if (!isAuthenticated) {
            if (!guestName.trim())  { toast.error("Укажите ваше имя"); return false; }
            if (!guestPhone.trim()) { toast.error("Укажите номер телефона"); return false; }
            if (guestPhone.replace(/\D/g, "").length < 10) { toast.error("Введите корректный номер телефона"); return false; }
        }
        if (isDelivery) {
            if (!street.trim()) { toast.error("Укажите адрес доставки"); return false; }
            if (belowMinOrder)  { toast.error(`Минимальная сумма для доставки — ${minOrderAmount} ₽`); return false; }
        }
        if (!isDelivery && time === "custom") {
            if (!customTime) { toast.error("Укажите время для заказа"); return false; }
            if (customTime < minRestaurantTime) { toast.error("Нельзя указать прошедшее время"); return false; }
            if (customTime > workingHoursEnd) { toast.error(`Ресторан работает до ${workingHoursEnd}`); return false; }
        }
        if (isDelivery && deliverySlots.length === 0) { toast.error("Доставка сегодня недоступна — нерабочее время"); return false; }
        if (!isDelivery && (peopleCount < 1 || peopleCount > 20)) { toast.error("Укажите корректное количество персон (1–20)"); return false; }
        return true;
    };

    const buildDishes = () => cartItems.map(item => ({
        dish_id:     item.id,
        quantity:    item.quantity,
        price:       item.price,
        modifiers:   item.modifiers || [],
        total_price: item.price * item.quantity,
    }));

    const buildInfo = () => {
        const info = isDelivery
            ? (selectedSlot ? { time: selectedSlot } : { asap: true })
            : (time === "custom" ? { time: customTime } : { asap: true });
        if (tableId) {
            info.table_id = tableId;
            if (tableName) info.table_name = tableName;
        }
        return info;
    };

    const submitOrderAsGuest = async () => {
        localStorage.setItem(GUEST_NAME_KEY, guestName.trim());
        localStorage.setItem(GUEST_PHONE_KEY, guestPhone.trim());

        const body = {
            guestName:      guestName.trim(),
            guestPhone:     guestPhone.trim(),
            restaurantID:   restaurant.id,
            amount:         totalWithDelivery,
            note:           comment,
            countPerson:    isDelivery ? 1 : peopleCount,
            dishes:         buildDishes(),
            delivery:       buildDeliveryPayload(),
            info:           buildInfo(),
            payment_method: paymentMethod,
        };

        const response = await fetch(`${appConfig.endpoint}/orders/create-guest`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (!data.success) throw new Error(data.errors ? JSON.stringify(data.errors) : "Ошибка при создании заказа");

        login(data.user, data.token);
        return data;
    };

    const submitOrderAsUser = async () => {
        const body = {
            restaurantID:   restaurant.id,
            userID:         user.id,
            amount:         totalWithDelivery,
            note:           comment,
            countPerson:    isDelivery ? 1 : peopleCount,
            dishes:         buildDishes(),
            delivery:       buildDeliveryPayload(),
            info:           buildInfo(),
            payment_method: paymentMethod,
        };

        const data = await authFetch(`${appConfig.endpoint}/orders/create`, {
            method: "POST",
            body: JSON.stringify(body),
        });

        if (!data.success) throw new Error(data.error || "Ошибка при создании заказа");
        return data;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const result = isAuthenticated ? await submitOrderAsUser() : await submitOrderAsGuest();
            const nextAction = result.next_action ?? 'wait';
            if (nextAction === 'pay') {
                navigate("/payment", { state: { order: result.order } });
            } else if (nextAction === 'success') {
                navigate("/order-success", { state: { order: result.order } });
            } else {
                navigate("/order-waiting", { state: { order: result.order, paymentMethod } });
            }
        } catch (error) {
            console.error("Ошибка при оформлении заказа:", error);
            toast.error(error.message || "Ошибка при оформлении заказа");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (authLoading || !restaurant) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader className="animate-spin text-brandBlue" size={28} />
            </div>
        );
    }

    if (cartItems.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600 mb-4">Корзина пуста</p>
                    <button onClick={() => navigate("/")} className="bg-brandBlue text-white px-6 py-2.5 rounded-xl text-sm font-medium">
                        К меню
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header restaurantName={restaurant?.name} />

            <div className="max-w-screen-md mx-auto px-4 pb-36 pt-5 page-enter">
                {/* Назад + заголовок */}
                <div className="flex items-center gap-3 mb-5">
                    <BackButton onClick={() => navigate("/cart")} disabled={isSubmitting} />
                    <h1 className="text-xl font-bold text-gray-900">Оформление заказа</h1>
                </div>

                {/* Бейдж столика */}
                {tableId && (
                    <div className="flex items-center gap-2.5 bg-brandBlue/8 border border-brandBlue/20 rounded-2xl px-4 py-3 mb-4">
                        <QrCode size={18} className="text-brandBlue shrink-0" />
                        <div>
                            <p className="text-sm font-semibold text-brandBlue">Заказ к столу</p>
                            <p className="text-xs text-brandBlue/70">{tableName || `Стол #${tableId}`}</p>
                        </div>
                    </div>
                )}

                {/* Состав заказа */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center">
                                <UtensilsCrossed size={16} className="text-gray-400" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-900">Ваш заказ</p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {cartItems.length} {cartItems.length === 1 ? "позиция" : cartItems.length < 5 ? "позиции" : "позиций"}
                                </p>
                            </div>
                        </div>
                        <span className="text-lg font-bold text-gray-900">{totalPrice} ₽</span>
                    </div>
                </div>

                {/* Контактные данные (только для гостей) */}
                {!isAuthenticated && (
                    <SectionCard icon={Users} title="Ваши данные">
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1.5 ml-1">Имя *</label>
                                <input
                                    type="text"
                                    value={guestName}
                                    onChange={(e) => setGuestName(e.target.value)}
                                    disabled={isSubmitting}
                                    placeholder="Иван"
                                    className={fieldClass}
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1.5 ml-1">Телефон *</label>
                                <input
                                    type="tel"
                                    value={guestPhone}
                                    onChange={(e) => setGuestPhone(e.target.value)}
                                    disabled={isSubmitting}
                                    placeholder="+7 900 123-45-67"
                                    className={fieldClass}
                                />
                            </div>
                        </div>
                    </SectionCard>
                )}

                {/* Способ получения — скрыт при заказе с QR-столика */}
                {!tableId && (
                <SectionCard icon={UtensilsCrossed} title="Способ получения">
                    <SegmentedControl
                        options={[
                            { value: "restaurant", label: "В ресторане" },
                            { value: "delivery",   label: "Доставка" },
                        ]}
                        value={orderType}
                        onChange={setOrderType}
                        disabled={isSubmitting || !deliveryEnabled}
                    />

                    {/* Инфо о доставке */}
                    {isDelivery && (
                        <div className="mt-3 space-y-2">
                            {deliveryCost > 0 && (
                                <div className="flex items-center justify-between text-sm px-1">
                                    <div className="flex items-center gap-1.5 text-gray-500">
                                        <Bike size={14} />
                                        Стоимость доставки
                                    </div>
                                    <span className="font-semibold text-gray-800">{deliveryCost} ₽</span>
                                </div>
                            )}
                            {deliveryCost === 0 && (
                                <div className="flex items-center gap-1.5 text-sm text-green-600 px-1">
                                    <Bike size={14} />
                                    Бесплатная доставка
                                </div>
                            )}
                            {minOrderAmount > 0 && (
                                <div className={`flex items-center justify-between text-sm px-1 ${belowMinOrder ? "text-red-500" : "text-gray-400"}`}>
                                    <div className="flex items-center gap-1.5">
                                        {belowMinOrder && <AlertCircle size={14} />}
                                        Минимальная сумма
                                    </div>
                                    <span className="font-medium">{minOrderAmount} ₽</span>
                                </div>
                            )}
                            {belowMinOrder && (
                                <div className="bg-red-50 border border-red-100 rounded-xl px-3 py-2 text-xs text-red-600">
                                    Добавьте ещё на {minOrderAmount - totalPrice} ₽ для оформления доставки
                                </div>
                            )}
                        </div>
                    )}
                </SectionCard>
                )}

                {/* Способ оплаты */}
                <SectionCard icon={CreditCard} title="Способ оплаты">
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { value: "cash",   label: "Наличными", icon: Banknote,    show: cashPaymentEnabled },
                            { value: "online", label: "Онлайн",    icon: CreditCard,  show: true },
                        ].filter(m => m.show).map(({ value, label, icon: Icon }) => (
                            <button
                                key={value}
                                onClick={() => setPaymentMethod(value)}
                                disabled={isSubmitting}
                                className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                                    paymentMethod === value
                                        ? "border-brandBlue bg-brandBlue/5 text-brandBlue"
                                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                                } disabled:opacity-50`}
                            >
                                <Icon size={16} />
                                {label}
                            </button>
                        ))}
                    </div>
                </SectionCard>

                {/* Адрес доставки */}
                {isDelivery && (
                    <SectionCard icon={MapPin} title="Адрес доставки">
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1.5 ml-1">Улица и дом *</label>
                                <input
                                    type="text"
                                    value={street}
                                    onChange={(e) => setStreet(e.target.value)}
                                    disabled={isSubmitting}
                                    placeholder="ул. Ленина, 42"
                                    className={fieldClass}
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1.5 ml-1">Квартира</label>
                                    <input
                                        type="text"
                                        value={apartment}
                                        onChange={(e) => setApartment(e.target.value)}
                                        disabled={isSubmitting}
                                        placeholder="15"
                                        className={fieldClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1.5 ml-1">Подъезд</label>
                                    <input
                                        type="text"
                                        value={entrance}
                                        onChange={(e) => setEntrance(e.target.value)}
                                        disabled={isSubmitting}
                                        placeholder="2"
                                        className={fieldClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1.5 ml-1">Этаж</label>
                                    <input
                                        type="text"
                                        value={floor}
                                        onChange={(e) => setFloor(e.target.value)}
                                        disabled={isSubmitting}
                                        placeholder="5"
                                        className={fieldClass}
                                    />
                                </div>
                            </div>
                        </div>
                    </SectionCard>
                )}

                {/* Время */}
                <SectionCard icon={Clock} title="Время">
                    {!isDelivery && (
                        <p className="text-xs text-gray-400 -mt-2 mb-3">
                            Ресторан работает: {workingHoursStart} – {workingHoursEnd}
                        </p>
                    )}
                    {isDelivery ? (
                        deliverySlots.length === 0 ? (
                            <p className="text-sm text-gray-400 text-center py-1">
                                Доставка сегодня недоступна — нерабочее время
                            </p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setSelectedSlot(null)}
                                    disabled={isSubmitting}
                                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 ${
                                        selectedSlot === null
                                            ? "bg-brandBlue text-white shadow-sm"
                                            : "bg-gray-100 text-gray-600"
                                    }`}
                                >
                                    ⚡ Сейчас
                                </button>
                                {deliverySlots.map(slot => (
                                    <button
                                        key={slot}
                                        onClick={() => setSelectedSlot(slot)}
                                        disabled={isSubmitting}
                                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 ${
                                            selectedSlot === slot
                                                ? "bg-brandBlue text-white shadow-sm"
                                                : "bg-gray-100 text-gray-600"
                                        }`}
                                    >
                                        {slot}
                                    </button>
                                ))}
                            </div>
                        )
                    ) : (
                        <>
                            <SegmentedControl
                                options={[
                                    { value: "asap",   label: "Как можно скорее" },
                                    { value: "custom", label: "Назначить время" },
                                ]}
                                value={time}
                                onChange={setTime}
                                disabled={isSubmitting}
                            />
                            {time === "custom" && (
                                <input
                                    type="time"
                                    value={customTime}
                                    min={minRestaurantTime}
                                    max={workingHoursEnd}
                                    onChange={(e) => setCustomTime(e.target.value)}
                                    disabled={isSubmitting}
                                    className={`${fieldClass} mt-3`}
                                />
                            )}
                        </>
                    )}
                </SectionCard>

                {/* Количество персон — только для ресторана */}
                {!isDelivery && (
                    <SectionCard icon={Users} title="Количество персон">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">Гостей за столом</span>
                            <div className="flex items-center gap-3 bg-gray-50 rounded-2xl border border-gray-200 p-1">
                                <button
                                    onClick={() => setPeopleCount(p => Math.max(1, p - 1))}
                                    disabled={isSubmitting || peopleCount <= 1}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-500 hover:bg-white hover:shadow-sm transition-all disabled:opacity-30"
                                >
                                    <Minus size={14} strokeWidth={2.5} />
                                </button>
                                <span className="text-base font-bold text-gray-900 min-w-[28px] text-center">{peopleCount}</span>
                                <button
                                    onClick={() => setPeopleCount(p => Math.min(20, p + 1))}
                                    disabled={isSubmitting || peopleCount >= 20}
                                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-brandBlue text-white hover:bg-brandBlue/90 transition-colors disabled:opacity-30"
                                >
                                    <Plus size={14} strokeWidth={2.5} />
                                </button>
                            </div>
                        </div>
                    </SectionCard>
                )}

                {/* Комментарий */}
                <SectionCard icon={MessageSquare} title="Комментарий">
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        disabled={isSubmitting}
                        placeholder={isDelivery
                            ? "Например: домофон не работает, звонить по телефону"
                            : "Например: «Положите побольше соуса» или «Поставьте детский стульчик»"}
                        className={`${fieldClass} resize-none`}
                    />
                </SectionCard>
            </div>

            {/* Фиксированная кнопка */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-4 shadow-lg z-10">
                <div className="max-w-screen-md mx-auto">
                    {/* Разбивка суммы при доставке */}
                    {isDelivery && deliveryCost > 0 && (
                        <div className="flex justify-between text-xs text-gray-400 mb-2 px-1">
                            <span>Заказ {totalPrice} ₽ + доставка {deliveryCost} ₽</span>
                            <span className="font-semibold text-gray-700">= {totalWithDelivery} ₽</span>
                        </div>
                    )}
                    <div className="flex items-center gap-4">
                        <div className="shrink-0">
                            <p className="text-xs text-gray-400">{isDelivery && deliveryCost > 0 ? "Итого с доставкой" : "Итого"}</p>
                            <p className="text-lg font-bold text-gray-900 leading-tight">{totalWithDelivery} ₽</p>
                        </div>
                        <PrimaryButton
                            onClick={handleSubmit}
                            disabled={isSubmitting || authLoading || belowMinOrder}
                            className="flex-1 flex items-center justify-center gap-2 py-3.5"
                        >
                            {isSubmitting
                                ? <Loader className="animate-spin" size={18} />
                                : paymentMethod === "online" ? "Перейти к оплате" : "Оформить заказ"
                            }
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderPage;
